import React, { useCallback, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
  useRowSelect,
} from "react-table";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { IoCaretForward, IoCaretBack } from "react-icons/io5";
import { BsThreeDots } from "react-icons/bs";
import TableSearch from "./TableSearch";
import styles from "../../assets/styles/Button.module.scss";
import PageLoader from "../PageLoader";
import MimaText from "../MimaText";

const propTypes = {
  tableData: PropTypes.array,
  tableColumns: PropTypes.array,
  searchPlaceholder: PropTypes.string,
  searchVariant: PropTypes.oneOf(["regular", "wide"]),
  totalItems: PropTypes.number,
  onLimitChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isFetching: PropTypes.bool,
  limit: PropTypes.number,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  setSearchQuery: PropTypes.func,
  selectedRows: PropTypes.array,
  onSelectedRowsChange: PropTypes.func,
};

const defaultProps = {
  tableData: [],
  tableColumns: [],
  searchPlaceholder: "search",
  searchVariant: "regular",
  onLimitChange: () => {},
  isLoading: false,
  isFetching: false,
  limit: 50,
  currentPage: 0,
  setCurrentPage: () => {},
  setSearchQuery: () => {},
  selectedRows: [],
  onSelectedRowsChange: () => {},
};

const MimaCheckboxTable = ({
  tableData,
  tableColumns,
  searchPlaceholder,
  searchVariant,
  totalItems,
  onLimitChange,
  isLoading,
  isFetching,
  limit,
  currentPage,
  setCurrentPage,
  setSearchQuery,
  selectedRows,
  onSelectedRowsChange,
  ...props
}) => {
  const data = useMemo(() => tableData, [tableData]);
  const columns = useMemo(() => tableColumns, [tableColumns]);
  const usedLimit = useMemo(() => limit, [limit]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    pageOptions,
    state,
    setGlobalFilter,
    setPageSize,
    prepareRow,
    gotoPage,
    pageCount,
    selectedFlatRows,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: usedLimit },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: ({ getToggleAllRowsSelectedProps }) => (
            <div>
              <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
            </div>
          ),
          Cell: ({ row }) => (
            <div>
              <input type="checkbox" {...row.getToggleRowSelectedProps()} />
            </div>
          ),
        },
        ...columns,
      ]);
    }
  );

  useEffect(() => {
    if (selectedRows.length !== selectedFlatRows.length) {
      onSelectedRowsChange(selectedFlatRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlatRows, selectedRows]);

  const { globalFilter, pageSize } = state;

  const maxDisplayedPages = 3;

  const totalPages = totalItems ? Math.ceil(totalItems / pageSize) : pageCount;

  const pageButtons = useMemo(() => {
    let pageButtons = [];
    if (totalPages <= maxDisplayedPages) {
      pageButtons = Array.from({ length: totalPages }, (_, index) => index);
      return pageButtons;
    } else {
      const startPage = Math.max(
        0,
        currentPage - Math.floor(maxDisplayedPages / 2)
      );
      const endPage = Math.min(
        totalPages - 1,
        startPage + maxDisplayedPages - 1
      );

      if (startPage > 0) {
        pageButtons.push("...");
      }

      pageButtons = pageButtons.concat(
        Array.from(
          { length: endPage - startPage + 1 },
          (_, index) => startPage + index
        )
      );

      if (endPage < totalPages - 1) {
        pageButtons.push("...");
      }
      return pageButtons;
    }
  }, [totalPages, maxDisplayedPages, currentPage]);

  return (
    <>
      <div className="searchInMiddle">
        <TableSearch
          filter={globalFilter}
          setFilter={setGlobalFilter}
          placeholder={searchPlaceholder}
          variant={searchVariant}
          setSearchQuery={setSearchQuery}
        />
      </div>

      <div className="whiteTable">
        {isLoading ? (
          <PageLoader loading={isLoading} title="while we fetch your data" />
        ) : (
          <table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      {column.render("Header")}
                      <span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <FaArrowDown />
                          ) : (
                            <FaArrowUp />
                          )
                        ) : (
                          ""
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {tableData.length > 0 ? (
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => {
                        return (
                          <td {...cell.getCellProps()}>
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            ) : (
              <MimaText variant="subtitleBold" mt={2} align="center">
                No Data Available
              </MimaText>
            )}
          </table>
        )}
      </div>

      {isFetching && <PageLoader loading={isFetching} type="bar" />}

      <div className={styles.pagination__group}>
        <div className={styles.pagination}>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              onLimitChange(Number(e.target.value));
            }}
          >
            {[50, 200, 300, 400, 500].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <strong>
            {currentPage + 1} of {totalItems ? totalPages : pageOptions.length}
          </strong>
        </div>
        <div className={styles.pagination}>
          <button
            onClick={() => {
              previousPage();
              setCurrentPage(currentPage - 1);
            }}
            disabled={currentPage === 0}
            className={styles.numberedPage}
          >
            <IoCaretBack style={{ fontSize: "2rem" }} />
          </button>
          {pageButtons.map((page, index) =>
            page === "..." ? (
              <BsThreeDots key={index} style={{ fontSize: "2rem" }} />
            ) : (
              <button
                key={index}
                onClick={() => {
                  gotoPage(page);
                  setCurrentPage(page);
                }}
                className={`${styles.numberedPage} ${currentPage === page ? styles.numberPageActive : ""}`}
              >
                {page + 1}
              </button>
            )
          )}
          <button
            onClick={() => {
              nextPage();
              setCurrentPage(currentPage + 1);
            }}
            disabled={currentPage === totalPages - 1}
            className={styles.numberedPage}
          >
            <IoCaretForward style={{ fontSize: "2rem" }} />
          </button>
        </div>
      </div>
    </>
  );
};

MimaCheckboxTable.propTypes = propTypes;
MimaCheckboxTable.defaultProps = defaultProps;
MimaCheckboxTable.displayName = "MimaCheckboxTable";

export default MimaCheckboxTable;
